import React, { forwardRef } from 'react';
import { numberToWords } from '../utils/formatCurrency';
import { LOGO_DATA_URL } from '../utils/logoData';

const fmt = (n) => '\u20B9' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
};

const LabelValue = ({ label, value }) => (
  <div style={{ fontSize: '8.5px', color: '#555', lineHeight: 1.5 }}>
    <span style={{ fontWeight: 700, color: '#000' }}>{label}</span> {value}
  </div>
);

const PAYMENT_METHODS = {
  cash: 'Cash', bank_transfer: 'Bank Transfer', upi: 'UPI', cheque: 'Cheque', other: 'Other'
};

const ReceiptPreview = forwardRef(({ form }, ref) => {
  const cin = (form.companyCIN || '').replace(/^CIN:\s*/i, '');
  const gstin = (form.companyGSTIN || '').replace(/^GSTIN:\s*/i, '');
  const amount = parseFloat(form.amount) || 0;

  return (
    <div ref={ref} style={{
      width: '210mm', height: '297mm', margin: '0 auto', padding: '12mm 14mm',
      fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", fontSize: '9px',
      color: '#000', background: '#fff', boxSizing: 'border-box', lineHeight: 1.45,
      display: 'flex', flexDirection: 'column',
    }}>

      {/* ── Logo + RECEIPT ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5mm' }}>
        <img src={LOGO_DATA_URL} alt="Logo" style={{ width: '48mm', height: 'auto' }} />
        <div style={{ fontSize: '24px', fontWeight: 700, color: '#333', letterSpacing: '1px' }}>RECEIPT</div>
      </div>

      {/* ── Company + Receipt meta ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4mm' }}>
        <div style={{ maxWidth: '58%' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '1.5mm' }}>{form.companyName}</div>
          {form.companyAddress && (
            <div style={{ fontSize: '8.5px', color: '#555', lineHeight: 1.5 }}>
              <span style={{ fontWeight: 700, color: '#000' }}>Head Office:</span><br />
              {form.companyAddress.split('\n').map((l, i) => <span key={i}>{l}<br /></span>)}
            </div>
          )}
          {form.companyPhone && <LabelValue label="Ph:" value={form.companyPhone} />}
          {form.companyEmail && <LabelValue label="Email:" value={form.companyEmail} />}
          {cin && <LabelValue label="CIN:" value={cin} />}
          {gstin && <LabelValue label="GSTIN:" value={gstin} />}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '11px', color: '#666' }}># {form.receiptNumber}</div>
          <div style={{ marginTop: '2mm' }}>
            <div style={{ fontSize: '8px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Amount Received</div>
            <div style={{ fontSize: '20px', fontWeight: 700, marginTop: '1mm' }}>{fmt(amount)}</div>
          </div>
        </div>
      </div>

      <div style={{ borderTop: '1px solid #ddd', margin: '3mm 0' }} />

      {/* ── Received From + Details ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '5mm' }}>
        <div style={{ maxWidth: '50%' }}>
          <div style={{ fontSize: '7.5px', color: '#888', textTransform: 'uppercase', marginBottom: '1mm' }}>Received From</div>
          {form.receivedFrom && form.receivedFrom.split('\n').map((l, i) => (
            <div key={i} style={i === 0 ? { fontSize: '12px', fontWeight: 700 } : { fontSize: '9px', color: '#333' }}>{l}</div>
          ))}
        </div>
        <table style={{ fontSize: '8.5px', borderCollapse: 'collapse' }}>
          <tbody>
            {[
              ['Receipt Date :', fmtDate(form.receiptDate)],
              ['Payment Method :', PAYMENT_METHODS[form.paymentMethod] || form.paymentMethod],
              form.referenceNumber && ['Reference No :', form.referenceNumber],
              form.invoiceRef && ['Against Invoice :', form.invoiceRef],
            ].filter(Boolean).map(([lbl, val], i) => (
              <tr key={i}>
                <td style={{ padding: '1mm 5mm 1mm 0', color: '#888' }}>{lbl}</td>
                <td style={{ padding: '1mm 0', textAlign: 'right', fontWeight: 500 }}>{val}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Amount Details ── */}
      <div style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: '2mm', padding: '5mm', marginBottom: '5mm' }}>
        <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ padding: '2mm 0', fontWeight: 700, fontSize: '11px' }}>Amount Received</td>
              <td style={{ padding: '2mm 0', textAlign: 'right', fontWeight: 700, fontSize: '14px' }}>{fmt(amount)}</td>
            </tr>
          </tbody>
        </table>
        <div style={{ marginTop: '2mm', fontSize: '8.5px' }}>
          <span style={{ color: '#888' }}>In Words: </span>
          <span style={{ fontWeight: 700 }}>{numberToWords(amount)}</span>
        </div>
      </div>

      {/* ── Notes ── */}
      {form.notes && (
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '3mm', marginBottom: '4mm' }}>
          <div style={{ fontSize: '9px', fontWeight: 700, marginBottom: '2mm' }}>Notes</div>
          <div style={{ fontSize: '8.5px', color: '#555', whiteSpace: 'pre-line' }}>{form.notes}</div>
        </div>
      )}

      <div style={{ flex: 1 }} />

      {/* ── Footer ── */}
      <div style={{ borderTop: '1px solid #eee', paddingTop: '2mm', fontSize: '7.5px', color: '#aaa', textAlign: 'center' }}>
        This is a computer-generated receipt and is valid without a signature.
      </div>
    </div>
  );
});

ReceiptPreview.displayName = 'ReceiptPreview';
export default ReceiptPreview;
